import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Helper pour générer les URL d'API
const getApiUrl = (endpoint) => {
  const baseUrl = process.env.NODE_ENV === 'production' 
    ? 'https://website-alturion-production.up.railway.app' 
    : 'http://localhost:5000';
  return `${baseUrl}${endpoint}`;
};

// Vérifier la date d'expiration des articles du panier
const checkCartItemsExpiration = (cartItems) => {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  return cartItems.filter(item => {
    if (!item.addedAt) {
      item.addedAt = new Date().toISOString();
      return true;
    }
    return new Date(item.addedAt) >= thirtyDaysAgo;
  });
};

// Nettoyer les doublons du panier
const cleanupCartItems = (cartItems) => {
  const seen = new Set();
  return cartItems.filter(item => {
    const duplicate = seen.has(item.product);
    seen.add(item.product);
    return !duplicate;
  });
};

const cartItemsFromStorage = localStorage.getItem('cartItems')
  ? JSON.parse(localStorage.getItem('cartItems'))
  : [];

const shippingAddressFromStorage = localStorage.getItem('shippingAddress')
  ? JSON.parse(localStorage.getItem('shippingAddress'))
  : {};

const paymentMethodFromStorage = localStorage.getItem('paymentMethod')
  ? JSON.parse(localStorage.getItem('paymentMethod'))
  : '';

const initialState = {
  cartItems: cartItemsFromStorage,
  shippingAddress: shippingAddressFromStorage,
  paymentMethod: paymentMethodFromStorage,
  loading: false,
  error: null,
};

export const addToCartAsync = createAsyncThunk(
  'cart/addToCartAsync',
  async ({ id, qty }, { getState, rejectWithValue }) => {
    try {
      const { data } = await axios.get(getApiUrl(`/api/products/${id}`));
      return {
        product: data._id,
        slug: data.slug,
        name: data.name,
        image: data.images && data.images.length > 0 ? data.images[0] : '/images/placeholder.png',
        images: data.images,
        price: data.price,
        requiresLicense: data.requiresLicense !== false,
        countInStock: data.countInStock || 100,
        downloadLink: data.downloadLink,
        qty,
      };
    } catch (error) {
      return rejectWithValue(
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message
      );
    }
  }
);

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    saveShippingAddress: (state, action) => {
      state.shippingAddress = action.payload;
      localStorage.setItem('shippingAddress', JSON.stringify(action.payload));
    },
    savePaymentMethod: (state, action) => {
      state.paymentMethod = action.payload;
      localStorage.setItem('paymentMethod', JSON.stringify(action.payload));
    },
    clearCartItems: (state) => {
      state.cartItems = [];
      localStorage.setItem('cartItems', JSON.stringify([]));
    },
    removeCartItem: (state, action) => {
      state.cartItems = state.cartItems.filter(
        (x) => x.product !== action.payload
      );
      localStorage.setItem('cartItems', JSON.stringify(state.cartItems));
    },
    updateCartItemQty: (state, action) => {
      const { id, qty } = action.payload;
      const item = state.cartItems.find((x) => x.product === id);
      if (item) {
        item.qty = qty;
        localStorage.setItem('cartItems', JSON.stringify(state.cartItems));
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addToCartAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToCartAsync.fulfilled, (state, action) => {
        state.loading = false;
        const item = action.payload;
        const existItem = state.cartItems.find((x) => x.product === item.product);
        
        if (existItem) {
          state.cartItems = state.cartItems.map((x) =>
            x.product === existItem.product ? item : x
          );
        } else {
          state.cartItems.push(item);
        }
        
        // Nettoyer le panier
        state.cartItems = cleanupCartItems(checkCartItemsExpiration(state.cartItems));
        
        localStorage.setItem('cartItems', JSON.stringify(state.cartItems));
      })
      .addCase(addToCartAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { 
  saveShippingAddress, 
  savePaymentMethod, 
  clearCartItems, 
  removeCartItem, 
  updateCartItemQty 
} = cartSlice.actions;

export default cartSlice.reducer; 